import React, { useMemo } from "react";
//#region redux
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { Preset } from "models/utilTypes/selectionTypes";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import {
  DimensionData,
  DimensionPresetSelectorProps,
  DimensionPresetSelectorState,
} from "models/propTypes/dimensionPropTypes";
import { AuthEnums, DialogEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import SplitPresetsAddFormContainer from "./splitPresetsAddFormContainer";
import PopMenuEvents from "../menu/popMenuEvents";
import StyledButton from "../buttons/styledButton";
import StyledMenu from "../menu/styledMenu";
import Icon from "../icon";
import { useDimensionService } from "serviceHooks";
//#endregion
import { CompanyService } from "services";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const DimensionPresetSelector: React.FC<DimensionPresetSelectorProps> = (props) => {
  const dimensionService = useDimensionService();
  const [state, setState] = React.useState<DimensionPresetSelectorState>({
    presets: [],
  });

  React.useEffect(() => {
    const isEqual =
      state.selectedPreset &&
      state.selectedPreset.presetRows.length === props.selectedItems.length &&
      state.selectedPreset.presetRows.every((p) => {
        const item = props.selectedItems.find((s) => s.id === p.id);
        return item !== undefined && +(item.data as DimensionData).percentage === p.percentage;
      });

    if (!isEqual) {
      setState({
        ...state,
        selectedPreset: undefined,
      });
    }
  }, [props.selectedItems]);

  React.useEffect(() => {
    getPresets();
  }, [props.dimensionType]);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const getPresets = () => {
    dimensionService.getPresets(props.dimensionPresetType, props.dimensionType).then((res) => {
      const presets = res.map((preset) => {
        return {
          id: preset.id,
          text: preset.name,
          presetRows: preset.percentages?.map((row) => ({
            id: row.dimensionId,
            percentage: row.percentage,
          })),
        } as Preset;
      });
      setState({
        ...state,
        presets: presets,
      });
    });
  };

  const menuItemList = useMemo(() => {
    return state.presets.map((preset, index) => ({
      id: preset.id,
      text: preset.text,
      selected: state.selectedPreset && preset.id === state.selectedPreset.id,
    }));
  }, [state.presets, state.selectedPreset]);

  const actionsList = [
    {
      disabled: state.selectedPreset !== undefined || props.selectedItems.length === 0 || !props.isValid,
      text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveDistribution),
      icon: IconDefinitionConstants.WPlus,
      onClickHandler: () => dispatch(displayDialog(savePresetDialog)),
    },
  ];

  const onPresetFormSubmitted = (preset: Preset) => {
    setState({
      ...state,
      presets: [...state.presets, preset],
      selectedPreset: preset,
    });
  };

  const savePresetDialog: DialogProps = useMemo(
    () => ({
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveDistributionKey),
      titleIcon: IconDefinitionConstants.WSplit,
      dialogType: DialogEnums.DialogType.Content,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
        },
      ],
      children: (
        <SplitPresetsAddFormContainer
          preset={{
            id: 0,
            text: "",
            presetRows: props.selectedItems.map((item) => {
              const data = item.data as DimensionData;
              return {
                id: data.dimensionId,
                percentage: data.percentage,
              };
            }),
          }}
          presets={state.presets}
          dimensionPresetType={props.dimensionPresetType}
          dimensionType={props.dimensionType}
          onFormSubmitted={onPresetFormSubmitted}
        />
      ),
    }),
    [props.selectedItems, props.dimensionPresetType, props.dimensionType, state.presets]
  );

  const onMenuItemClicked = (id: number) => {
    const selectedPreset = state.presets.find((preset) => preset.id === id);
    if (selectedPreset !== undefined) {
      props.onPresetSelected(selectedPreset);
      setState({
        ...state,
        selectedPreset: selectedPreset,
      });
    }
  };

  return (
    <React.Fragment>
      <PopMenuEvents
        render={(anchorEl, isOpen, openMenu, closeMenu) => (
          <React.Fragment>
            <StyledButton
              aria-controls="customized-menu"
              aria-haspopup="true"
              size="xs"
              className={cssExports.iconButton}
              onClick={openMenu}
              disabled={CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Auditor)}
            >
              <Icon icon={IconDefinitionConstants.WSplit} />
            </StyledButton>
            <StyledMenu
              anchorEl={anchorEl}
              closeMenu={closeMenu}
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.UseDistribution)}
              onMenuClickHandler={onMenuItemClicked}
              itemList={menuItemList}
              actionList={actionsList}
            />
          </React.Fragment>
        )}
      />
    </React.Fragment>
  );
};

export default DimensionPresetSelector;
